import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";

// mui components
import { Box, Stack } from "@mui/material";

// custom components
import LoanInfoCard from "../../info-card";
import RequiredError from "../../required-error";
import SecondaryButton from "../../buttons/secondary-button";
import PrimaryButton from "../../buttons/primary-button";
import SupportingDocs from "../../supporting-docs";
import Heading5 from "../../typography/heading-05";
import Body3 from "../../typography/body-03";
import Body2 from "../../typography/body-02";

// helper utils
import { formatBytes } from "../../../utils/format-bytes";
import { selectedUserWithLoanChannel } from "../../../utils/common-helpers";
import {
	uploadFileWithChunking,
} from "../../../utils/upload-file-with-chunks";

// custom modal
import LoadingDataModal from "../../modal/loading-data-modal";
import SuccessModal from "../../modal/success-modal";
import ErrorModal from "../../modal/error-modal";

// mui icons
import { ArrowBackOutlined, DoneOutlined } from "@mui/icons-material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";

// api slices
import { useFieldMutation } from "../../../features/field-values/fieldValueApiSlice";
import { useLoanDocsMutation } from "../../../features/loan-docs/loanDocsApiSlice";
import { useUpdateFieldValuesTpoMutation } from "../../../features/update-field-value-tpo/updateFieldsApiSlice";
import { useMoveLoanMutation } from "../../../features/move-loan/moveLoanApiSlice";
import { usePopulateFieldsMutation } from "../../../features/populate-fields/populateFieldsApiSlice";

// reducer slices
import { setLoanDocsReducer } from "../../../features/loan-docs/loanDocsSlice";
import { selectImpersonatedUser } from "../../../features/admin/impersonated-user/impersonatedUserSlice";

// reducer slices
import {
	selectLoEmail,
	selectUserDetails,
	selectSelectedOrganization,
	selectOrganizationName,
} from "../../../features/auth/authSlice";

// custom styles
import styles from "./index.module.scss";
import useRegisterLoanSection from "../../../hooks/useRegisterLoanSection";

const fileTypes = ["PDF", "PNG", "JPEG", "JPG", "HTML", "TXT"];

export const LoanDocuments = ({
	setLoanApplicationStage,
	loanGuid,
	currentStage,
	validateAllSectionsForSubmit,
	loanSteps,
	registerSectionHandler,
	markSectionCompletion,
	directPath,
}) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	let { id } = useParams();

	const loEmail = useSelector(selectLoEmail);
	const userDetails = useSelector(selectUserDetails);
	const impersonatedUser = useSelector(selectImpersonatedUser);
	const selectedOrganization = useSelector(selectSelectedOrganization);
	const organizationName = useSelector(selectOrganizationName);

	// Determine the current organization for API calls
	const currentOrganization = selectedOrganization || organizationName;
	const activeLoanGuid = loanGuid || id;

	const [loanEmail, setLoanEmail] = useState("");
	const [loanOrganization, setLoanOrganization] = useState("");

	console.log("60 user details:", userDetails);
	console.log("60 lo email:", loEmail);
	console.log("60 impersonated user:", impersonatedUser?.email);

	console.log("23 id:", id);

	const [successModalVisible, setSuccessModalVisible] = useState(false);
	const [errorModalVisible, setErrorModalVisible] = useState(false);

	const handleOpenSuccessModal = () => {
		setSuccessModalVisible(true);
	};

	const handleCloseSuccessModal = () => {
		setSuccessModalVisible(false);
		navigate("/pipeline");
	};

	const handleGoToUploadHistory = () => {
		setSuccessModalVisible(false);
		navigate("/new-loan", { state: { openHistory: true } });
	};

	const handleOpenErrorModal = () => {
		setErrorModalVisible(true);
	};

	const handleCloseErrorModal = () => {
		setErrorModalVisible(false);
		navigate("/pipeline");
	};

	const [field] = useFieldMutation();
	const [loanDocs, { isLoading }] = useLoanDocsMutation();

	const [updateFieldValuesTpo, { isLoading: isUpdatingFields }] =
		useUpdateFieldValuesTpoMutation();
	const [moveLoan, { isLoading: isMovingLoan }] = useMoveLoanMutation();
	const [populateFields, { isLoading: isPopulatingFields }] =
		usePopulateFieldsMutation();

	const [file, setFile] = useState("");
	const [error, setError] = useState("");
	const [uploadedFiles, setUploadedFiles] = useState([]);

	const handleFileUpload = async (file) => {
		setFile(file);
		console.log("34 file:", file);
		console.log("34 file length:", file?.length);
		let newArr = [];
		Array.from(file).forEach((file) => newArr.push(file));
		setUploadedFiles([...uploadedFiles, ...newArr]);
	};

	const deleteFile = (event, index) => {
		console.log("43 files:", uploadedFiles, index);

		let newArr = uploadedFiles;
		newArr.splice(index, 1);

		setUploadedFiles([...newArr]);
	};

	const handleNavigateAfterDelay = () => {
		setTimeout(() => {
			navigate("/pipeline");
		}, 3000); // 3 seconds
	};

	const handlePrevClick = () => {
		console.log("67 prev click");
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: "smooth",
		});
		setLoanApplicationStage(currentStage - 1);
	};

	const isExecutiveOrManager =
		userDetails?.user_roles?.includes("executive") ||
		userDetails?.user_roles?.includes("manager") ||
		userDetails?.user_roles?.includes("AE") ||
		userDetails?.user_roles?.includes("AM") ||
		userDetails?.user_roles?.includes("PA") ||
		userDetails?.user_roles?.includes("assistant");

	const appendChunkMetadata = (formData, meta) => {
		if (!meta?.isChunked) return;

		formData.append("chunk_index", String(meta?.chunkIndex));
		formData.append("total_chunks", String(meta?.totalChunks));
		formData.append("chunk_size", String(meta?.chunkSize));
		formData.append("upload_id", meta?.uploadId);
		formData.append("original_file_name", meta?.originalFileName);
		formData.append("original_file_size", String(meta?.originalFileSize));
		formData.append("is_chunked", "true");
		formData.append("is_first_chunk", String(meta?.isFirstChunk));
		formData.append("is_last_chunk", String(meta?.isLastChunk));
		formData.append("chunk_name", meta?.chunkName);
	};

	const uploadLoanDocumentFile = async (element) =>
		uploadFileWithChunking({
			file: element,
			uploadPart: async ({ file: uploadFile, meta }) => {
				const formData = new FormData();
				if (id !== undefined && id !== "") {
					formData.append("loan_guid", id);
				} else if (loanGuid !== undefined && loanGuid !== "") {
					formData.append("loan_guid", loanGuid);
				}
				formData.append(
					"file",
					uploadFile,
					meta?.chunkName || uploadFile?.name || element?.name
				);
				appendChunkMetadata(formData, meta);
				const fileUpload = await loanDocs({
					formData,
					selectedOrganization: currentOrganization,
					userDetails,
					user_email: userDetails?.email,
				}).unwrap();

				return fileUpload;
			},
		});

	const getErrorMessage = (err) =>
		err?.data?.message?.details ?? err?.data?.message ?? "";

	const isAlreadyMovedError = (message) =>
		typeof message === "string" && message.toLowerCase().includes("already moved");

	const handleSubmitSuccess = async () => {
		markSectionCompletion?.(currentStage, true);
		setError("");

		handleOpenSuccessModal();
	};

	const handleSubmitFiles = async () => {
		console.log("50 submit click!!!", file, uploadedFiles);

		if (validateAllSectionsForSubmit) {
			const { isValid, incompleteSectionNames } = validateAllSectionsForSubmit(loanSteps);
			if (!isValid) {
				const sectionList = incompleteSectionNames?.length
					? ` The following sections are incomplete: ${incompleteSectionNames.join(", ")}.`
					: "";
				setError(`Please provide a value for all required fields.${sectionList}`);
				return;
			}
		}

		for (let index = 0; index < uploadedFiles.length; index++) {
			const element = uploadedFiles[index];

			// dispatch API and open modal if successful
			try {
				const fileUpload = await uploadLoanDocumentFile(element);
				console.log("99 file data:", fileUpload);
				dispatch(setLoanDocsReducer(fileUpload));

				if (index === uploadedFiles.length - 1) {
					// run at the end of the loop
					let valuesArr = [
						{ id: "TPO.X4", value: DateTime.now().toFormat("MM-dd-yyyy") },
						{ id: "TPO.X90", value: DateTime.now().toFormat("MM-dd-yyyy") },
						{
							id: "CX.LC.DOC.REC.DATE",
							value: DateTime.fromISO(DateTime.now(), {
								zone: "America/New_York",
							}).toFormat("MM-dd-yyyy hh:mm a"),
						},
					];

					console.log("191 loan guid:", loanGuid);

					const updateFieldValues = await updateFieldValuesTpo({
						loan_guid: loanGuid,
						field_values: valuesArr,
						selectedOrganization: currentOrganization,
						userDetails
					}).unwrap();

					console.log("90 updated values:", updateFieldValues);

					try {
						const moveLoanValue = await moveLoan({
							loan_guid: loanGuid,
							selectedOrganization: currentOrganization,
							userDetails
						}).unwrap();
						console.log("90 updated values:", moveLoanValue);
					} catch (moveErr) {
						const moveMessage = getErrorMessage(moveErr);
						// Loan already in Pipeline is still a successful submission.
						if (!isAlreadyMovedError(moveMessage)) {
							throw moveErr;
						}
						console.log("moveLoan already in Pipeline:", moveMessage);
					}

					let userEmail;

					console.log("200 isExecutiveOrManager:", isExecutiveOrManager);

					if (isExecutiveOrManager) {
						userEmail = loanEmail;
					} else if (
						loEmail !== null &&
						loEmail !== undefined &&
						loEmail !== ""
					) {
						userEmail = loEmail;
					} else if (
						impersonatedUser?.email !== null &&
						impersonatedUser?.email !== undefined &&
						impersonatedUser?.email !== ""
					) {
						userEmail = impersonatedUser?.email;
					} else {
						userEmail = userDetails?.email;
					}

					console.log("210 user email:", userEmail);

					let regex = /\(([^)]+)\)/;

					console.log("215 org loan channel:", loanOrganization);
					console.log("215 loan channel:", loanOrganization?.match(regex)?.[1]);

					const loanChannel = isExecutiveOrManager
						? loanOrganization?.match(regex)?.[1]?.toUpperCase()
						: selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
								?.loan_channel;

					// Ensure loan_channel is a string, not an array
					const loanChannelValue = Array.isArray(loanChannel) ? loanChannel[0] : loanChannel;

					try {
						const populateCustomFields = await populateFields({
							loan_guid: loanGuid,
							email: userEmail,
							loan_channel: loanChannelValue,
							selectedOrganization: typeof currentOrganization === 'object' ? currentOrganization?.name : currentOrganization,
							userDetails: userDetails
						}).unwrap();
						console.log("150 populateCustomFields:", populateCustomFields);
					} catch (populateErr) {
						// Loan is already moved; do not block success on populate failure.
						console.log("populateFields error after move:", populateErr);
					}

					await handleSubmitSuccess();
				}
			} catch (err) {
				console.log("95 err:", err);
				const message = getErrorMessage(err);

				if (isAlreadyMovedError(message)) {
					await handleSubmitSuccess();
					return;
				}

				setError(message);
			}
		}
	};

	const findFieldValue = (fieldValues, fieldId) => {
		return fieldValues?.find((o) => o.fieldId === fieldId)?.value;
	};

	const updateField = (fieldValues) => {
		console.log("250 field values:", fieldValues);

		setLoanEmail(findFieldValue(fieldValues, "3968"));
		setLoanOrganization(findFieldValue(fieldValues, "TPO.X14"));
	};

	const getFieldValues = async (loanId) => {
		console.log("135 loan id:", loanId);
		try {
			const getValues = await field({
				loan_guid: loanId,
				fields: "3968,TPO.X14,TPO.X62,TPO.X75",
				selectedOrganization: currentOrganization,
				userDetails
			}).unwrap();
			console.log("265 all fields:", getValues?.response);
			if (getValues?.response?.length > 0) {
				updateField(getValues?.response);
			}
		} catch (err) {
			console.log("270 error:", err);
		}
	};

	useEffect(() => {
		console.log("275 loan Guid:", loanGuid);

		if (loanGuid !== undefined && loanGuid !== "") {
			getFieldValues(loanGuid);
		}
	}, [loanGuid]);

	const highlightSectionErrors = useCallback(() => {
		if (!uploadedFiles?.length) {
			setError("Please provide a value for all required fields.");
		}
	}, [uploadedFiles]);

	useEffect(() => {
		if (uploadedFiles?.length > 0) {
			markSectionCompletion?.(currentStage, true);
		}
	}, [uploadedFiles, currentStage, markSectionCompletion]);

	useRegisterLoanSection(registerSectionHandler, {
		validate: () => uploadedFiles?.length > 0,
		save: async () => true,
		highlightErrors: highlightSectionErrors,
	});

	return (
		<Stack direction="column" className={styles.stackContainer}>
			<Stack className={styles.bodyContainer}>
				<LoanInfoCard loanId={loanGuid} />

				<Stack direction="column" className={styles.cardHeaderContainer}>
					<Stack direction="row" className={styles.headerLinkContainer}>
						<Heading5 text="Upload Supporting Documents" fontType="semibold" />
					</Stack>

					<Body3 text="Please refer to the Resources Tab for our minimum required submission docs per loan product/program when uploading" />
				</Stack>

				{/* loan flow error */}
				{error !== undefined && error !== "" && (
					<RequiredError
						headerText={
							error.toLowerCase?.().includes("already moved")
								? ""
								: "Missing fields"
						}
						text={error}
					/>
				)}

				<SupportingDocs
					fileCount={uploadedFiles?.length}
					fileTypes={fileTypes}
					text="or drag to upload supporting files"
					handleFileAttach={handleFileUpload}
					multiple={true}
				/>

				{uploadedFiles !== undefined && uploadedFiles?.length > 0 && (
					<Stack direction="column" className={styles.uploadedFileContainer}>
						{uploadedFiles?.map((file, index) => (
							<Stack
								direction="row"
								key={index}
								className={styles.fileListContainer}
							>
								<Box className={styles.fileIconContainer}>
									<UploadFileIcon className={styles.fileIcon} />
								</Box>
								<Stack direction="column" className={styles.fileNameContainer}>
									<Body2 text={file?.name} />
									<Body3 text={formatBytes(file?.size)} />
								</Stack>

								<Box
									sx={{ cursor: "pointer" }}
									onClick={(event) => deleteFile(event, index)}
									className={styles.deleteIconContainer}
								>
									<CloseIcon className={styles.deleteIcon} />
								</Box>
							</Stack>
						))}
					</Stack>
				)}
			</Stack>

			{/* footer buttons */}
			<Stack direction="row" className={styles.footerContainer}>
				<SecondaryButton
					text="Previous"
					startIcon={<ArrowBackOutlined className={styles.prevIcon} />}
					onClick={handlePrevClick}
				/>
				<PrimaryButton
					text="Submit Application"
					endIcon={<DoneOutlined className={styles.nextIcon} />}
					onClick={handleSubmitFiles}
					disabled={
						uploadedFiles?.length === 0 ||
						isLoading ||
						isUpdatingFields ||
						isMovingLoan ||
						isPopulatingFields
					}
				/>
			</Stack>

			{/* modals */}
			<LoadingDataModal
				open={
					isLoading || isUpdatingFields || isMovingLoan || isPopulatingFields
				}
				text="Please wait while loan is being created"
			/>
			<SuccessModal
				open={successModalVisible}
				text="Loan Created Successfully"
				handleClose={handleCloseSuccessModal}
				confirmText="Go to Pipeline"
				secondaryText="Upload History"
				onSecondary={handleGoToUploadHistory}
				secondaryDisabled={isExecutiveOrManager}
			/>
			<ErrorModal
				open={errorModalVisible}
				text="This loan has been successfully submitted to the Lender already"
				handleClose={handleCloseErrorModal}
			/>
		</Stack>
	);
};

export default LoanDocuments;
